import { Injectable } from '@angular/core';
import {
    AddCustomerGQL,
    AddCustomerInput,
    Customer,
    CustomerByIdGQL,
    CustomerFilterInput,
    CustomersGQL,
    CustomerSortInput,
    DeleteCustomerGQL,
    UpdateCustomerGQL,
    UpdateCustomerInput,
} from '../../../../../graphql/generated';
import { Subject, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { GraphqlPaginatorResult } from '@knalgeel/pandora';
import { awaitLoading } from '../../../shared/rxjs/await-loading';

interface GetCustomersInput {
    after?: string;
    first?: number;
    where?: CustomerFilterInput;
    order?: [CustomerSortInput];
}

@Injectable({
    providedIn: 'root',
})
export class CustomerService {

    private readonly _created = new Subject<string>();
    public readonly created$ = this._created.asObservable();

    private readonly _updated = new Subject<string>();
    public readonly updated$ = this._updated.asObservable();

    private readonly _deleted = new Subject<string>();
    public readonly deleted$ = this._deleted.asObservable();

    constructor(
        private readonly deleteCustomerCommandGQL: DeleteCustomerGQL,
        private readonly updateCustomerCommandGQL: UpdateCustomerGQL,
        private readonly addCustomerCommandGQL: AddCustomerGQL,
        private readonly customerByIdGQL: CustomerByIdGQL,
        private readonly customersGQL: CustomersGQL,
    ) {
    }

    public createCustomer(input: AddCustomerInput) {
        return this.addCustomerCommandGQL.mutate({ input }, {
            refetchQueries: [
                this.customersGQL.document,
            ],
        }).pipe(
            awaitLoading(),
            map(response => response.data.addCustomer.addedCustomerId),
        );
    }

    public updateCustomer(input: UpdateCustomerInput) {
        return this.updateCustomerCommandGQL.mutate({ input }, {
            refetchQueries: [
                this.customersGQL.document,
            ],
        }).pipe(
            awaitLoading(),
            map(response => response.data.updateCustomer.updatedCustomerId),
            tap(customer => this._updated.next(customer)),
        );
    }

    public deleteCustomer(id: string) {
        return this.deleteCustomerCommandGQL.mutate({ customerId: id }, {
            refetchQueries: [
                this.customersGQL.document,
            ],
        }).pipe(
            awaitLoading(),
            map(response => response.data.deleteCustomer.deletedCustomerId),
            tap(id => this._deleted.next(id)),
        );
    }

    public getCustomerById(id: string) {
        return this.customerByIdGQL.watch({ customerId: id }).valueChanges.pipe(
            awaitLoading(),
            map(response => response.data.customerById),
        );
    }

    public getCustomers(input: GetCustomersInput) {
        return this.customersGQL.watch(input, { fetchPolicy: 'cache-and-network' }).valueChanges.pipe(
            awaitLoading(),
            map(response => response.data.customers as GraphqlPaginatorResult<Customer>),
        );
    }

}
